import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import App from "@/App";

// HttpOnly cookie auth cutover (2026-05-15): every request must send
// credentials so the access_token + refresh_token cookies travel with it.
// Backend reads either the cookie (preferred, XSS-safe) or the legacy
// Authorization: Bearer header during the cutover window.
axios.defaults.withCredentials = true;

// --- Sentry (optional — only initialised if REACT_APP_SENTRY_DSN is set) ---
//
// Code-split dynamic import: webpack bundles @sentry/react into a separate
// chunk that only downloads when a DSN is configured, so builds without a DSN
// pay nothing. @sentry/react is a real dependency (package.json), so webpack
// resolves this at build time. (A previous version wrapped the import in
// eval() to dodge the build, but that also stopped the browser resolving the
// bare module name at runtime — so Sentry never actually loaded.)
//
// REACT_APP_* vars are inlined by CRA at BUILD time, so REACT_APP_SENTRY_DSN
// must be set on the frontend service before the build, and a frontend DSN is
// public by design (it ships in the browser bundle).
if (process.env.REACT_APP_SENTRY_DSN) {
  import("@sentry/react")
    .then(({ init, browserTracingIntegration }) => {
      init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
        release: process.env.REACT_APP_SENTRY_RELEASE,
        integrations:
          typeof browserTracingIntegration === "function"
            ? [browserTracingIntegration()]
            : [],
        tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.1),
        sendDefaultPii: false, // UK GDPR
      });
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("Sentry failed to initialise:", err);
    });
}

const container = document.getElementById("root");
const app = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Build-time prerendering (scripts/prerender.js) writes static HTML snapshots
// for the marketing pages. When a snapshot is present the container already
// has markup, so we hydrate it in place (no flash, reuses the server DOM).
// Otherwise this is a normal client-only load, so mount a fresh root.
if (container.hasChildNodes()) {
  ReactDOM.hydrateRoot(container, app);
} else {
  ReactDOM.createRoot(container).render(app);
}

// --- Service worker registration ---
// Registers /service-worker.js once the page has loaded. The SW handles
// offline shell, asset caching, and lets the app be "Add to Home Screen"
// installable on Android/iOS. Only runs in production builds so dev
// hot-reload isn't interfered with.
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        // Listen for updates and reload once the new SW takes control.
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // A new version is ready. Soft-reload so the user picks
              // it up on the next navigation — they can also do nothing
              // and the next full reload will activate it naturally.
              // eslint-disable-next-line no-console
              console.info("New Ariv version available — refresh to update.");
            }
          });
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("Service worker registration failed:", err);
      });
  });
}
